import { FC } from 'react';
import {
  RelatedItemsReadOnlyTag,
  RelatedItemsSection,
} from 'components/related-items/related-items-section';
import { WorkspaceCard, type WellScope } from 'components/workspace-cards';

interface Props {
  workspaceId: number;
  /** The scoped well's UWI — the link's host inside this workspace. */
  uwi: string;
  promoted: boolean;
  scope: WellScope;
}

/**
 * Related Items — the one user edit Epic 1 keeps.
 *
 * New links are staged against the well inside this workspace, so the canonical well's
 * page only shows them after Promote. The list also reads through to the well's existing
 * items, which are shown badged and read-only. The backend refuses writes once promoted,
 * so the section drops to read-only rather than letting a click 422. The surface itself
 * is the shared RelatedItemsSection; this supplies the host and draws the one heading —
 * the section's own "Related Items (N)" is hidden so the card is not headed twice.
 * @param {Props} props The workspace, the scoped well, and the card's stepper.
 * @return {JSX.Element} The card.
 */
export const RelatedDocumentsCard: FC<Props> = ({
  workspaceId,
  uwi,
  promoted,
  scope,
}) => (
  <WorkspaceCard
    title="Related Items"
    scope={scope}
    // No "Staged" tag: the list mixes staged rows with the well's own, so a card-level
    // label would misdescribe half of them. The per-row badge carries it instead.
    actions={promoted ? <RelatedItemsReadOnlyTag /> : null}
  >
    <RelatedItemsSection
      // RelatedItemsSection appends `/related_items`, so this is the staged well's own
      // path. Built here rather than read off the envelope because the staged well does
      // not yet carry a HATEOAS `resources.relatedItems` href — see the note to BE.
      host={{ basePath: `/workspaces/${workspaceId}/staged_wells/${uwi}` }}
      readOnly={promoted}
      hideHeading
    />
  </WorkspaceCard>
);
